/**
 * `<NextStepCoach/>` — the calm, progressive "what do I do next?" strip for the
 * lead's Map editor (issue #980).
 *
 * Why: the one-time empty-workspace coach-mark (#932) teaches the four-step
 * loop, but vanishes the instant a root node exists — exactly when the lead
 * faces the hard, un-intuitive part (decomposing the outcome). From live use,
 * newcomers stall here: they don't break the outcome down, don't assign anyone,
 * and don't move state, because nothing on the canvas tells them to.
 *
 * This strip mounts below the editor toolbar and shows ONE next move, computed
 * from the live tree by the pure `resolveNextStep`. It ADVANCES as the lead
 * acts — break the outcome down and it switches to "assign"; assign someone and
 * it switches to "move state"; once the loop is rolling it self-hides. It is a
 * gentle teacher, not an alert: soft-mint surface, teal accent, factual copy,
 * never alarm colour (§A9).
 *
 * It is dismissible. Dismiss persists per-team in `localStorage` (a lead who
 * already knows the loop on one team shouldn't be nagged), and once dismissed
 * the strip stays gone for that team — it never re-surfaces on a later stage.
 *
 * Brand firewall: no per-person language, no scores/tallies, no tier wording,
 * the strategic term stays internal. The copy is about the MAP's shape, mirror-
 * ing the empty-workspace loop. It never names or ranks a member (§A9).
 *
 * Auto-layout (§A7): this is chrome ABOVE the canvas — it sets no node x/y and
 * does not fight the tree layout.
 */

import { useCallback, useEffect, useState } from "react";

import type { TreeNode } from "../tree/buildTree";
import { PALETTE } from "../tree/palette";
import { type NextStep, resolveNextStep } from "./nextStep";

/** One calm line of guidance per non-terminal loop stage. */
const COACH_COPY: Record<Exclude<NextStep, "underway">, { title: string; detail: string }> = {
  decompose: {
    title: "Now break it down.",
    detail:
      "What are the 3–6 steps to reach this outcome? Focus the outcome card and add a sub-task for each — go as many layers deep as you need.",
  },
  assign: {
    title: "Now put people on it.",
    detail:
      "Focus a task and choose “@ assign” so each piece has an owner — assignees can then grow and update their own branch.",
  },
  "set-state": {
    title: "Move state as work happens.",
    detail:
      "Focus a task and set it In progress or Done. The map stays current so everyone can see where things stand.",
  },
};

/** localStorage key for the per-team dismiss flag. */
function dismissKey(teamId: string | undefined): string {
  return `tarii.taskmap.nextStepCoach.dismissed.${teamId ?? "unknown"}`;
}

function readDismissed(teamId: string | undefined): boolean {
  try {
    return globalThis.localStorage?.getItem(dismissKey(teamId)) === "1";
  } catch {
    // Private mode / disabled storage → just show the coach (never throw).
    return false;
  }
}

export interface NextStepCoachProps {
  /** The live tree roots (the coach reads the map's shape from these). */
  roots: TreeNode[];
  /** The current team id — scopes the per-team dismiss flag. */
  teamId: string | undefined;
}

export function NextStepCoach({ roots, teamId }: NextStepCoachProps) {
  // The dismiss flag is per-team; re-read it when the team changes so a fresh
  // team's coach isn't suppressed by a dismiss on a previous one.
  const [dismissed, setDismissed] = useState(() => readDismissed(teamId));
  useEffect(() => {
    setDismissed(readDismissed(teamId));
  }, [teamId]);

  const dismiss = useCallback(() => {
    setDismissed(true);
    try {
      globalThis.localStorage?.setItem(dismissKey(teamId), "1");
    } catch {
      // Best-effort persistence; the in-session state still hides the strip.
    }
  }, [teamId]);

  const step = resolveNextStep(roots);

  // Nothing to teach: empty tree (empty workspace owns it), loop underway, or
  // the lead dismissed it for this team.
  if (step === null || step === "underway" || dismissed) return null;

  const { title, detail } = COACH_COPY[step];

  return (
    <div
      data-testid="next-step-coach"
      data-step={step}
      className="flex items-start gap-3 border-b px-4 py-2.5"
      style={{ borderColor: PALETTE.softBorder, backgroundColor: "#F7FFF9" }}
    >
      <span
        aria-hidden
        className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-bold"
        style={{ backgroundColor: PALETTE.teal, color: "#FFFFFF" }}
      >
        ✦
      </span>
      <div className="min-w-0 flex-1">
        <span className="text-[12px] font-semibold" style={{ color: PALETTE.ink }}>
          {title}
        </span>
        <span className="ml-1.5 text-[12px]" style={{ color: PALETTE.muted }}>
          {detail}
        </span>
      </div>
      <button
        type="button"
        data-testid="next-step-coach-dismiss"
        onClick={dismiss}
        aria-label="Dismiss this tip"
        title="Dismiss this tip"
        className="shrink-0 rounded px-2 py-0.5 text-[11px] transition-colors hover:bg-white"
        style={{ color: PALETTE.faint }}
      >
        Got it
      </button>
    </div>
  );
}
